/**
 * Custom hook for managing tag-related logic
 */

import { IFormField, ITagGenerationSettings } from '@ViewModels';
import { useCallback, useMemo } from 'react';
import { DEFAULT_TAG_GENERATION_SETTINGS } from '../constants/questionFieldConstants';
import { TagVisualization } from '../TagManager';

interface UseTagManagementProps {
	tagManagerProps?: {
		tagGenerationSettings?: Partial<ITagGenerationSettings>;
	};
	field: IFormField<string>;
	isOptionField: boolean;
}

export const useTagManagement = ({ tagManagerProps, field, isOptionField }: UseTagManagementProps) => {
	// Check if this field has a tag prefix
	const hasTagPrefix = useMemo(() => {
		if (!tagManagerProps?.tagGenerationSettings?.customFormFieldTagSettings) return false;
		return tagManagerProps.tagGenerationSettings.customFormFieldTagSettings.some(
			setting => setting.fieldId === field.id && setting.shouldGenerateTag
		);
	}, [tagManagerProps?.tagGenerationSettings?.customFormFieldTagSettings, field.id]);

	// Memoize the tag generation settings to prevent recreation
	const tagGenerationSettings = useMemo(
		() => ({
			tagOnCompletion:
				tagManagerProps?.tagGenerationSettings?.tagOnCompletion || DEFAULT_TAG_GENERATION_SETTINGS.tagOnCompletion,
			completionTag:
				tagManagerProps?.tagGenerationSettings?.completionTag || DEFAULT_TAG_GENERATION_SETTINGS.completionTag,
			customFormFieldTagSettings:
				tagManagerProps?.tagGenerationSettings?.customFormFieldTagSettings ||
				DEFAULT_TAG_GENERATION_SETTINGS.customFormFieldTagSettings,
		}),
		[tagManagerProps?.tagGenerationSettings]
	);

	// Memoize the tag visualization component to prevent recreation
	const tagVisualization = useCallback(
		(optionLabel: string) => (
			<TagVisualization fieldId={field.id} optionLabel={optionLabel} tagGenerationSettings={tagGenerationSettings} />
		),
		[field.id, tagGenerationSettings]
	);

	// Determine if tag visualization should be provided
	const getTagVisualization = useCallback(() => {
		if (!tagManagerProps || !isOptionField || !hasTagPrefix) return undefined;
		return tagVisualization;
	}, [tagManagerProps, isOptionField, hasTagPrefix, tagVisualization]);

	return {
		hasTagPrefix,
		tagVisualization: getTagVisualization(),
		tagGenerationSettings,
	};
};
